import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Video from 'components/Video';
import Picture from 'components/Picture';
import type { MediaSize } from '@starly/starly-types';
import type { ChildRefFunc, MediaProps } from './types';

export const getBestMediaSize = (
  sizes: MediaSize[],
  desiredWidth: number,
  scale: number,
): MediaSize => {
  let currentItem = sizes[0];

  const originalWidth = desiredWidth * (scale > 0 ? scale : 1);

  if (currentItem.width < originalWidth) {
    for (let i = 0; i < sizes.length; i += 1) {
      currentItem = sizes[i];

      if (currentItem.width >= originalWidth) {
        break;
      }
    }
  }

  return currentItem;
};

const Media = forwardRef<ChildRefFunc, MediaProps>(({
  className,
  media,
  lazy,
  alt,
  onClick,
  onLoad,
  isMuted = true,
  autoplay = true,
  isVideoPlaying,
  'data-test-id': dataTestId,
  setMuted,
}, ref) => {
  const mediaRef = useRef<HTMLDivElement & ChildRefFunc>(null);
  const [mediaSize, setMediaSize] = useState<MediaSize>();
  useImperativeHandle(ref, () => ({
    pauseVideo() {
      mediaRef.current?.pauseVideo?.();
    },
    playVideo() {
      mediaRef.current?.playVideo?.();
    },
  }));
  /**
   * Load media according to placeholder width
   */
  useEffect(() => {
    if (mediaRef.current && media?.sizes) {
      const size = getBestMediaSize(
        media.sizes,
        mediaRef.current.offsetWidth,
        window.devicePixelRatio || 1,
      );

      setMediaSize(size);
    }
  }, [mediaRef, media]);

  if (media && (typeof media === 'string')) {
    return (
      <Picture
        data-test-id={`image_${dataTestId}`}
        ref={mediaRef}
        alt={alt}
        className={className}
        url={String(media)}
        onLoad={onLoad}
      />
    );
  }

  /**
   * Load image via Picture component
   */
  if (media?.type === 'image' && mediaSize?.url) {
    return (
      <Picture
        key={mediaSize.url}
        data-test-id={`image_${dataTestId}`}
        ref={mediaRef}
        alt={alt}
        className={className}
        url={mediaSize.url}
        lazy={lazy}
        onClick={onClick}
        onLoad={onLoad}
      />
    );
  }

  /**
   * Load video via Video component
   */
  if (media?.type === 'video' && mediaSize?.mp4) {
    return (
      <Video
        key={mediaSize.mp4}
        data-test-id={`video_${dataTestId}`}
        ref={mediaRef}
        className={className}
        loop
        muted={isMuted}
        autoplay={isVideoPlaying === undefined ? autoplay : isVideoPlaying}
        poster={mediaSize.screenshot}
        mp4={mediaSize.mp4}
        ogv={mediaSize.ogv}
        webm={mediaSize.webm}
        onClick={onClick}
        onLoad={onLoad}
        lazy={lazy}
        isVideoPlaying={isVideoPlaying}
        setMuted={setMuted}
      />
    );
  }

  /**
   * Fallback for the first load + invalid media
   */
  return (
    <div
      data-test-id="media_placeholder"
      ref={mediaRef}
      className={className}
    />
  );
});

export default Media;
